import * as React from 'react';
import * as Sentry from '@sentry/react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { constants, useAuthState } from './auth';
import { isMobileApp } from './isMobileApp';
import {
  useGetPushConfigLazyQuery,
  useRegisterPushSubscriptionMutation,
} from '@/generated/codegen';
import {
  arrayBufferToBase64Url,
  readRecordedServerKey,
  recordServerKey,
  urlBase64ToUint8Array,
} from './webPushKeys';

export type WebPushStatus =
  | 'loading'
  | 'unsupported'
  | 'prompt-required'
  | 'denied'
  | 'subscribed'
  | 'error';

type PushFailureStage = 'permission' | 'subscribe' | 'register' | 'unregister';

const webPushStatusAtom = atom<WebPushStatus>('loading');
// The endpoint of this device's current subscription, once known. The
// settings page needs it to query/mutate this device's own preferences —
// there is no other way to identify "this subscription" from the UI.
const webPushEndpointAtom = atom<string | undefined>(undefined);

export const useWebPushStatus = () => useAtomValue(webPushStatusAtom);
export const useWebPushEndpoint = () => useAtomValue(webPushEndpointAtom);

const isPushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof window !== 'undefined' &&
  'PushManager' in window;

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    nav.standalone === true
  );
};

// Subscription failures are the entire premise of this initiative: the
// previous push SDK swallowed them silently on unsupported browsers for years.
// Report every real failure to Sentry, tagged by stage and standalone-ness,
// so "iOS installed PWAs fail at 100%" is visible as a pattern rather than
// an absence of data. The 'unsupported' path is intentionally excluded —
// it is a normal state on browsers without the Push API, not a failure.
const reportPushFailure = (error: unknown, push_stage: PushFailureStage) => {
  Sentry.captureException(error, {
    tags: {
      push_stage,
      push_standalone: String(isStandalone()),
    },
  });
};

// The stable identity of this browser install (BIL-4930), minted once and
// persisted — same localStorage convention as the VAPID-key record above.
// Endpoints rotate at the push service's whim; this id is ours, so the
// server keys the subscription row by (user, device) and updates the
// endpoint in place — preferences survive every rotation, including the
// `pushsubscriptionchange` ones we never observe. A cleared localStorage
// mints a new device: acceptable, and no worse than the endpoint-keyed
// behaviour it replaces. Deliberately NOT cleared on sign-out — on a shared
// computer the server resolves ownership by claiming the endpoint.
const DEVICE_ID_STORAGE_KEY = 'BILDERIT_PUSH_DEVICE_ID';

const getOrCreateDeviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing !== null) return existing;
  const deviceId = crypto.randomUUID();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
  return deviceId;
};

/**
 * Ensures the current registration is subscribed with the up-to-date VAPID
 * key: reuses a matching existing subscription, re-subscribes if the key
 * rotated (comparing through the same base64url canonicalization on both
 * sides), or creates a fresh subscription. `userVisibleOnly: true` is
 * mandatory — Chrome and iOS both reject silent push.
 *
 * Re-subscribing is the destructive branch — it discards a working endpoint
 * and orphans the preferences stored against it (BIL-4920/4921/4923) — so it
 * only runs on positive evidence of rotation. When the browser discloses
 * `applicationServerKey`, that disclosure wins; when it discloses null, the
 * key recorded at subscribe() time fills in. An undisclosed key with no
 * record means "assume unchanged", never "assume rotated".
 */
const ensureSubscription = async (
  registration: ServiceWorkerRegistration,
  vapidPublicKey: string
): Promise<PushSubscription> => {
  const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
  const currentKeyEncoded = arrayBufferToBase64Url(applicationServerKey.buffer);

  const existing = await registration.pushManager.getSubscription();
  if (existing) {
    const disclosedKey = existing.options.applicationServerKey;
    const knownKeyEncoded =
      disclosedKey !== null
        ? arrayBufferToBase64Url(disclosedKey)
        : readRecordedServerKey(existing.endpoint);

    if (knownKeyEncoded === null || knownKeyEncoded === currentKeyEncoded) {
      recordServerKey(existing.endpoint, currentKeyEncoded);
      return existing;
    }
    await existing.unsubscribe();
  }

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });
  recordServerKey(subscription.endpoint, currentKeyEncoded);
  return subscription;
};

// Single-flight across every caller. Two subscribe flows routinely start
// within the same tick — the priming dialog's click handler and the
// handler's permission-change effect (Chrome reports the grant while the
// click's own flow is still running) — and each used to mint and register on
// its own: two registrations racing over the same endpoint. Module scope on
// purpose: the flow is used from more than one hook instance.
let subscribeInFlight: Promise<void> | null = null;

const useSubscribeFlow = () => {
  const setStatus = useSetAtom(webPushStatusAtom);
  const setEndpoint = useSetAtom(webPushEndpointAtom);
  const [fetchPushConfig] = useGetPushConfigLazyQuery();
  const [registerPushSubscription] = useRegisterPushSubscriptionMutation();

  const run = React.useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const { data } = await fetchPushConfig({ fetchPolicy: 'network-only' });
      const vapidPublicKey = data?.getPushConfig.vapidPublicKey;

      if (!vapidPublicKey) {
        throw new Error('pushConfig query returned no VAPID key');
      }

      const subscription = await ensureSubscription(
        registration,
        vapidPublicKey
      );

      // Register with an empty preference set — no topics, no defaults.
      // Derived audiences (e.g. machinist-reminder) come from the `role`
      // table server-side; there is nothing for this device to seed.
      await registerPushSubscription({
        variables: {
          input: {
            endpoint: subscription.endpoint,
            deviceId: getOrCreateDeviceId(),
            p256dh: arrayBufferToBase64Url(subscription.getKey('p256dh')),
            auth: arrayBufferToBase64Url(subscription.getKey('auth')),
            userAgent: navigator.userAgent,
            // The origin fixes which app a subscription belongs to — this is
            // the same build-time constant isMobileApp() uses elsewhere, not
            // a new env var. See the notification module's domain/app.ts.
            app: isMobileApp() ? 'maquinista' : 'manager',
          },
        },
      });

      setStatus('subscribed');
      setEndpoint(subscription.endpoint);
    } catch (error) {
      reportPushFailure(error, 'subscribe');
      setStatus('error');
    }
  }, [fetchPushConfig, registerPushSubscription, setEndpoint, setStatus]);

  return React.useCallback(() => {
    if (!subscribeInFlight) {
      subscribeInFlight = run().finally(() => {
        subscribeInFlight = null;
      });
    }
    return subscribeInFlight;
  }, [run]);
};

/**
 * Requests permission and subscribes. Must only be called from a real user
 * gesture (e.g. the click handler of the existing `NotificationsPrompt`
 * dialog) — browsers, and iOS in particular, silently reject permission
 * requests made outside one.
 */
export const useRegisterWebPush = () => {
  const setStatus = useSetAtom(webPushStatusAtom);
  const subscribe = useSubscribeFlow();

  return React.useCallback(async () => {
    if (!isPushSupported()) return;

    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch (error) {
      reportPushFailure(error, 'permission');
      setStatus('error');
      return;
    }

    if (permission !== 'granted') {
      setStatus(permission === 'denied' ? 'denied' : 'prompt-required');
      return;
    }

    await subscribe();
  }, [setStatus, subscribe]);
};

export const WebPushHandler: React.FC = () => {
  const { matches } = useAuthState();
  const setStatus = useSetAtom(webPushStatusAtom);
  const setEndpoint = useSetAtom(webPushEndpointAtom);
  const status = useAtomValue(webPushStatusAtom);
  const registeredEndpoint = useAtomValue(webPushEndpointAtom);
  const subscribe = useSubscribeFlow();

  // Reconciliation (field finding, 2026-09-10): Android Chrome rotates the
  // push endpoint SILENTLY — no `pushsubscriptionchange` for rotations
  // (Chrome ≥137 fires it only on permission re-grant, with null old/new).
  // The service worker keeps the browser side healthy, but the server row
  // still points at the dead endpoint until something re-registers, and
  // the first send to it 410-prunes the row: the device goes dark until the
  // next full page load. This closes the gap for any page that is open:
  // whenever the app comes back to the foreground (or the SW reports a
  // change, on the browsers that do fire the event), compare the browser's
  // live subscription with what we registered and re-register on mismatch.
  // It cannot help while no page is running — that is the parked "Layer B".
  const statusRef = React.useRef(status);
  const endpointRef = React.useRef(registeredEndpoint);
  statusRef.current = status;
  endpointRef.current = registeredEndpoint;
  const reconcilingRef = React.useRef(false);

  const reconcileSubscription = React.useCallback(
    async (reason: 'visibility' | 'sw-change') => {
      if (!isPushSupported()) return;
      if (statusRef.current !== 'subscribed') return;
      if (reconcilingRef.current) return;
      reconcilingRef.current = true;
      try {
        const registration = await navigator.serviceWorker.ready;
        const live = await registration.pushManager.getSubscription();
        const registered = endpointRef.current;
        if (live && live.endpoint === registered) return;

        // Counted, not alarmed: this is how we learn the real-world rotation
        // rate (the parked Layer B decision hangs on it).
        Sentry.captureMessage('push subscription drifted from the server row', {
          level: 'info',
          tags: {
            push_stage: 'rotation',
            push_rotation_reason: reason,
            push_rotation_live: String(Boolean(live)),
            push_standalone: String(isStandalone()),
          },
        });

        if (Notification.permission !== 'granted') {
          setStatus(
            Notification.permission === 'denied' ? 'denied' : 'prompt-required'
          );
          return;
        }
        // `subscribe` reuses the browser's live same-key subscription (or
        // mints one if it vanished) and re-registers it — the row heals.
        await subscribe();
      } catch (error) {
        reportPushFailure(error, 'register');
      } finally {
        reconcilingRef.current = false;
      }
    },
    [setStatus, subscribe]
  );

  React.useEffect(() => {
    if (!isPushSupported()) return;

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void reconcileSubscription('visibility');
      }
    };
    const onSwMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'SW_SUBSCRIPTION_CHANGED') return;
      void reconcileSubscription('sw-change');
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    // Capture the container: the cleanup must not re-read the global.
    const sw = navigator.serviceWorker;
    const canListenToSw = typeof sw?.addEventListener === 'function';
    if (canListenToSw) sw.addEventListener('message', onSwMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (canListenToSw) sw.removeEventListener('message', onSwMessage);
    };
  }, [reconcileSubscription]);

  // Bumped whenever the browser reports a notification-permission change, to
  // re-run the status effect below.
  const [permissionEpoch, setPermissionEpoch] = React.useState(0);

  // The service worker has no Sentry of its own, so an evaluation failure
  // there is invisible — that is how a browser where our `push` listener never
  // registered still looked healthy from the page. The worker posts problems
  // out; this is the only place they can reach Sentry.
  React.useEffect(() => {
    // Guard on the capability actually used, not on the container: jsdom and
    // partial stubs expose `serviceWorker` without `addEventListener`.
    if (typeof navigator.serviceWorker?.addEventListener !== 'function') return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'SW_PUSH_PROBLEM') return;
      Sentry.captureMessage(`service worker: ${event.data.message}`, {
        level: 'warning',
        tags: { push_stage: 'service-worker' },
      });
    };

    const sw = navigator.serviceWorker;
    sw.addEventListener('message', onMessage);
    return () => sw.removeEventListener('message', onMessage);
  }, []);

  // Notification permission can change entirely outside our UI: the padlock
  // menu, the browser's site settings, or a native prompt answered after ours
  // was dismissed. Without this the card stays stale until a reload — which is
  // exactly what a reviewer hit on PR #2064: permission granted, and the button
  // still read "Habilitar notificaciones".
  //
  // `permissions.query` is the only way to observe this; there is no
  // `Notification.onchange`. Every branch degrades to today's reload-to-refresh
  // behaviour rather than breaking: Safari < 16 has no Permissions API, and
  // some browsers reject the `notifications` descriptor outright.
  React.useEffect(() => {
    if (!isPushSupported()) return;
    if (typeof navigator.permissions?.query !== 'function') return;

    let permissionStatus: PermissionStatus | undefined;
    let cancelled = false;

    navigator.permissions
      .query({ name: 'notifications' as PermissionName })
      .then((status) => {
        if (cancelled) return;
        permissionStatus = status;
        status.onchange = () => setPermissionEpoch((epoch) => epoch + 1);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, []);

  React.useEffect(() => {
    if (!isPushSupported()) {
      // Expected on every desktop Safari visit — report nothing, or Sentry
      // fills up with a normal state rather than an actionable failure.
      setStatus('unsupported');
      return;
    }

    if (matches(constants.STATE_SIGNIN) || matches(constants.STATE_SIGNUP)) {
      // Only the browser-side unsubscribe runs here — deliberately. By the
      // time this branch runs there is no token (signOut() already reloaded
      // the page, and the session-expiry route into signIn is token-less by
      // definition), so a mutation requiring an authenticated owner could
      // never succeed here — there used to be one; it never worked. A
      // signed-out browser still must stop receiving pushes, hence keeping
      // unsubscribe(): the server row is left with a dead endpoint until the
      // next login re-registers it or a fan-out 410-prunes it, which is the
      // accepted consequence, not a bug.
      navigator.serviceWorker.ready
        .then((registration) => registration.pushManager.getSubscription())
        .then((subscription) => {
          if (!subscription) return;
          return subscription.unsubscribe();
        })
        .then(() => setEndpoint(undefined))
        .catch((error) => reportPushFailure(error, 'unregister'));
      return;
    }

    if (!matches({ [constants.STATE_LOGGED_IN]: 'idle' })) return;

    // Never call Notification.requestPermission() here — only act when
    // permission has already been granted through a user gesture elsewhere.
    if (Notification.permission !== 'granted') {
      setStatus(
        Notification.permission === 'denied' ? 'denied' : 'prompt-required'
      );
      return;
    }

    // Already subscribed means there is nothing to redo — endpoint drift is
    // the reconciliation effect's job, not this one's.
    if (statusRef.current === 'subscribed') return;

    subscribe();
  }, [
    matches,
    setEndpoint,
    setStatus,
    subscribe,
    // Re-resolves status when the browser reports a permission change, so
    // granting from outside our UI updates the card without a reload.
    permissionEpoch,
  ]);

  return null;
};
